import { useEffect, useRef, useState, ComponentType, ReactNode } from "react";

interface LazySectionProps {
  loader: () => Promise<{ default: ComponentType<object> } | ComponentType<object>>;
  fallback?: ReactNode;
  rootMargin?: string;
}

function SectionSkeleton() {
  return (
    <div className="w-full py-16 md:py-24 flex items-center justify-center">
      <div className="animate-pulse flex flex-col items-center gap-4">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="h-4 w-64 bg-muted rounded" />
      </div>
    </div>
  );
}

export function LazySection({ loader, fallback, rootMargin = "200px" }: LazySectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [LoadedComponent, setLoadedComponent] = useState<ComponentType<object> | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(currentRef);

    return () => observer.disconnect();
  }, [rootMargin]);

  useEffect(() => {
    if (isVisible && !LoadedComponent) {
      loader().then((mod) => {
        const Component = 'default' in mod ? mod.default : mod;
        setLoadedComponent(() => Component as ComponentType<object>);
      });
    }
  }, [isVisible, LoadedComponent, loader]);

  if (LoadedComponent) {
    return <LoadedComponent />;
  }

  return (
    <div ref={ref}>
      {fallback || <SectionSkeleton />}
    </div>
  );
}
